import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { classNames } from 'primereact/utils';
import companyCreditService from '../../services/companyCreditService';

const CompanyCreditManagement = () => {
    // State
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    // Sort
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);

    // Filter
    const [globalFilter, setGlobalFilter] = useState('');
    const [error, setError] = useState(null);

    // Company balance
    const [companyBalance, setCompanyBalance] = useState(0);

    // Transfer / Reclaim dialogs
    const [transferDialogVisible, setTransferDialogVisible] = useState(false);
    const [reclaimDialogVisible, setReclaimDialogVisible] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [transferCredits, setTransferCredits] = useState(null);
    const [reclaimCreditsAmount, setReclaimCreditsAmount] = useState(null);
    const [transferReason, setTransferReason] = useState('');
    const [reclaimReason, setReclaimReason] = useState('');
    const [transferNote, setTransferNote] = useState('');
    const [reclaimNote, setReclaimNote] = useState('');
    const [transferring, setTransferring] = useState(false);
    const [reclaiming, setReclaiming] = useState(false);
    const [transferSubmitted, setTransferSubmitted] = useState(false);
    const [reclaimSubmitted, setReclaimSubmitted] = useState(false);

    // Transactions dialog
    const [transactionsDialog, setTransactionsDialog] = useState(false);
    const [transactionsEmployee, setTransactionsEmployee] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsFirst, setTransactionsFirst] = useState(0);
    const [transactionsRows, setTransactionsRows] = useState(10);
    const [transactionsTotalRecords, setTransactionsTotalRecords] = useState(0);
    // Primitive filters (P14)
    const [txnTypeFilter, setTxnTypeFilter] = useState(null);
    const [txnDateFrom, setTxnDateFrom] = useState(null);
    const [txnDateTo, setTxnDateTo] = useState(null);

    const toast = useRef(null);

    // Load balance + employees on mount
    useEffect(() => {
        loadBalance();
        loadEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload employees on pagination / sort changes
    useEffect(() => {
        loadEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [first, rows, sortField, sortOrder]);

    // Load transactions when dialog opens or pagination/filters change (P14 — primitive deps only)
    useEffect(() => {
        if (transactionsDialog && transactionsEmployee) {
            loadEmployeeTransactions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionsDialog, transactionsEmployee, transactionsFirst, transactionsRows, txnTypeFilter, txnDateFrom, txnDateTo]);

    const loadBalance = async () => {
        try {
            const response = await companyCreditService.getBalance();
            if (response.success) {
                setCompanyBalance(response.data.balance || 0);
            }
        } catch (err) {
            console.error('Error loading balance:', err?.message || 'Unknown error');
        }
    };

    const loadEmployees = async () => {
        setLoading(true);
        setError(null);
        try {
            const page = Math.floor(first / rows) + 1;
            const params = {
                page,
                limit: rows,
                sort_field: sortField,
                sort_order: sortOrder === 1 ? 'asc' : 'desc',
                search: globalFilter || undefined,
            };
            Object.keys(params).forEach((key) => {
                if (params[key] === null || params[key] === undefined) delete params[key];
            });

            const response = await companyCreditService.getEmployeesCreditBalances(params);
            if (response.success) {
                setEmployees(response.data || []);
                setTotalRecords(response.pagination?.total || 0);
            } else {
                setEmployees([]);
                setTotalRecords(0);
            }
        } catch (err) {
            console.error('Error loading employees:', err?.message || 'Unknown error');
            setError('Failed to load employees. Please try again.');
            setEmployees([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const loadEmployeeTransactions = async () => {
        if (!transactionsEmployee) return;
        setTransactionsLoading(true);
        try {
            const page = Math.floor(transactionsFirst / transactionsRows) + 1;
            const formatDate = (date) => {
                if (!date) return null;
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                return `${y}-${m}-${d}`;
            };
            const params = {
                page,
                limit: transactionsRows,
                type: txnTypeFilter,
                date_from: formatDate(txnDateFrom),
                date_to: formatDate(txnDateTo),
            };
            const response = await companyCreditService.getEmployeeTransactions(transactionsEmployee.id, params);
            if (response.success) {
                setTransactions(response.data || []);
                setTransactionsTotalRecords(response.pagination?.total || 0);
            }
        } catch (err) {
            console.error('Error loading employee transactions:', err?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load employee transactions',
                life: 5000,
            });
        } finally {
            setTransactionsLoading(false);
        }
    };

    const onTransactionsPage = (e) => {
        setTransactionsFirst(e.first);
        setTransactionsRows(e.rows);
    };

    const handleFilterChange = () => {
        setFirst(0);
        loadEmployees();
    };

    const clearFilters = () => {
        setGlobalFilter('');
        setFirst(0);
        setTimeout(() => loadEmployees(), 0);
    };

    // Transfer
    const openTransferDialog = (employee) => {
        setSelectedEmployee(employee);
        setTransferCredits(null);
        setTransferReason('Company admin credit allocation');
        setTransferNote('');
        setTransferSubmitted(false);
        setTransferDialogVisible(true);
    };

    const closeTransferDialog = () => {
        setTransferDialogVisible(false);
        setSelectedEmployee(null);
        setTransferCredits(null);
        setTransferReason('');
        setTransferNote('');
        setTransferSubmitted(false);
    };

    const handleTransfer = async () => {
        setTransferSubmitted(true);
        if (!transferCredits || transferCredits <= 0 || !Number.isInteger(transferCredits)) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Please enter a valid integer credits amount (minimum 1)',
                life: 3000,
            });
            return;
        }
        if (transferCredits > companyBalance) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Insufficient Balance',
                detail: `Your company has ${companyBalance} credits available`,
                life: 4000,
            });
            return;
        }

        setTransferring(true);
        try {
            const response = await companyCreditService.transferCredits(
                selectedEmployee.id,
                transferCredits,
                transferReason || null,
                transferNote || null
            );
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Transfer Successful',
                    detail: `${transferCredits} credits transferred to ${selectedEmployee.name}`,
                    life: 4000,
                });
                closeTransferDialog();
                loadBalance();
                loadEmployees();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Transfer Failed',
                    detail: response.message || 'Failed to transfer credits',
                    life: 5000,
                });
            }
        } catch (err) {
            console.error('Transfer error:', err?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.response?.data?.message || 'Failed to transfer credits',
                life: 5000,
            });
        } finally {
            setTransferring(false);
        }
    };

    // Reclaim
    const openReclaimDialog = (employee) => {
        setSelectedEmployee(employee);
        setReclaimCreditsAmount(null);
        setReclaimReason('');
        setReclaimNote('');
        setReclaimSubmitted(false);
        setReclaimDialogVisible(true);
    };

    const closeReclaimDialog = () => {
        setReclaimDialogVisible(false);
        setSelectedEmployee(null);
        setReclaimCreditsAmount(null);
        setReclaimReason('');
        setReclaimNote('');
        setReclaimSubmitted(false);
    };

    const handleReclaim = async () => {
        setReclaimSubmitted(true);
        if (!reclaimCreditsAmount || reclaimCreditsAmount <= 0 || !Number.isInteger(reclaimCreditsAmount)) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Please enter a valid integer credits amount (minimum 1)',
                life: 3000,
            });
            return;
        }
        if (!reclaimReason || reclaimReason.trim().length < 5) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Reason is required (minimum 5 characters)',
                life: 3000,
            });
            return;
        }
        if (reclaimCreditsAmount > (selectedEmployee?.credit_balance || 0)) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Insufficient Balance',
                detail: `Employee only has ${selectedEmployee.credit_balance} credits`,
                life: 3000,
            });
            return;
        }

        setReclaiming(true);
        try {
            const response = await companyCreditService.reclaimCredits(
                selectedEmployee.id,
                reclaimCreditsAmount,
                reclaimReason,
                reclaimNote || null
            );
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Reclaim Successful',
                    detail: `${reclaimCreditsAmount} credits reclaimed from ${selectedEmployee.name}`,
                    life: 4000,
                });
                closeReclaimDialog();
                loadBalance();
                loadEmployees();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Reclaim Failed',
                    detail: response.message || 'Failed to reclaim credits',
                    life: 5000,
                });
            }
        } catch (err) {
            console.error('Reclaim error:', err?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.response?.data?.message || 'Failed to reclaim credits',
                life: 5000,
            });
        } finally {
            setReclaiming(false);
        }
    };

    // Transactions dialog
    const openTransactionsDialog = (employee) => {
        setTransactionsEmployee(employee);
        setTransactions([]);
        setTransactionsFirst(0);
        setTransactionsRows(10);
        setTransactionsTotalRecords(0);
        setTxnTypeFilter(null);
        setTxnDateFrom(null);
        setTxnDateTo(null);
        setTransactionsDialog(true);
    };

    const closeTransactionsDialog = () => {
        setTransactionsDialog(false);
        setTransactionsEmployee(null);
    };

    const resetTransactionFilters = () => {
        setTxnTypeFilter(null);
        setTxnDateFrom(null);
        setTxnDateTo(null);
    };

    // KPI card — uniform PrimeReact Card
    const KpiCard = ({ icon, label, value, description }) => (
        <div className="col-6 lg:col">
            <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
                <div className="flex justify-content-between align-items-start">
                    <div className="flex flex-column gap-1">
                        <span className="text-600 text-sm font-medium">{label}</span>
                        <span className="text-900 text-xl font-semibold">{value}</span>
                    </div>
                    <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                        <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                    </div>
                </div>
                <span className="text-500 text-xs block mt-2">{description}</span>
            </Card>
        </div>
    );

    // Column templates
    const nameBodyTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-medium text-sm text-900">{rowData.name}</span>
            <small className="text-500">{rowData.email}</small>
        </div>
    );

    const balanceBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-1">
            <span className="font-semibold text-primary">{(rowData.credit_balance || 0).toLocaleString()}</span>
            <span className="text-500 text-sm">credits</span>
        </div>
    );

    const actionBodyTemplate = (rowData) => (
        <div className="flex flex-wrap gap-1">
            <Button
                icon="pi pi-arrow-up"
                label="Transfer"
                severity="success"
                outlined
                size="small"
                onClick={() => openTransferDialog(rowData)}
                tooltip="Transfer credits to employee"
                tooltipOptions={{ position: 'top' }}
                aria-label={`Transfer credits to ${rowData.name}`}
            />
            <Button
                icon="pi pi-arrow-down"
                label="Reclaim"
                severity="warning"
                outlined
                size="small"
                onClick={() => openReclaimDialog(rowData)}
                tooltip="Reclaim credits from employee"
                tooltipOptions={{ position: 'top' }}
                aria-label={`Reclaim credits from ${rowData.name}`}
                disabled={!rowData.credit_balance || rowData.credit_balance === 0}
            />
            <Button
                icon="pi pi-list"
                label="History"
                severity="info"
                outlined
                size="small"
                onClick={() => openTransactionsDialog(rowData)}
                tooltip="View all transactions"
                tooltipOptions={{ position: 'top' }}
                aria-label={`View transaction history for ${rowData.name}`}
            />
        </div>
    );

    // Transaction templates
    const transactionTypeTemplate = (rowData) => {
        const isCredit = rowData.type === 1;
        return (
            <Tag
                value={isCredit ? 'Credit' : 'Debit'}
                severity={isCredit ? 'success' : 'danger'}
                icon={isCredit ? 'pi pi-plus' : 'pi pi-minus'}
            />
        );
    };

    const transactionAmountTemplate = (rowData) => {
        const isCredit = rowData.type === 1;
        return (
            <span className={classNames('font-semibold', isCredit ? 'text-green-600' : 'text-red-600')}>
                {isCredit ? '+' : '-'}{rowData.credits}
            </span>
        );
    };

    const transactionDateTemplate = (rowData) => {
        const date = new Date(rowData.created_at);
        const formatted = new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }).format(date);
        const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return (
            <div className="flex flex-column">
                <span className="text-700 text-sm">{formatted}</span>
                <small className="text-500">{time}</small>
            </div>
        );
    };

    const transferTypeLabels = {
        'tenant_admin_transfer': 'Admin Transfer',
        'tenant_admin_reclaim': 'Admin Reclaim',
        'company_admin_transfer': 'Company Transfer',
        'company_admin_reclaim': 'Company Reclaim',
        'api_debit': 'API Usage',
        'purchase': 'Purchase',
        'refund': 'Refund',
    };

    const transferTypeTemplate = (rowData) => {
        if (!rowData.transfer_type) return <span className="text-500">-</span>;
        return transferTypeLabels[rowData.transfer_type] || rowData.transfer_type;
    };

    const partyTemplate = (party) => {
        if (!party) return <span className="text-500">-</span>;
        return (
            <div className="flex flex-column">
                <span className="font-medium">{party.name}</span>
                <small className="text-500">{party.email}</small>
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header */}
                    <div className="mb-4">
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Company Credit Management</h1>
                        <p className="text-600 text-sm m-0">
                            Transfer or reclaim credits across your company employees
                        </p>
                    </div>

                    {/* KPI cards — 2 per row mobile, 2 per row desktop (wider since we only have 2) */}
                    <div className="grid mb-3">
                        <KpiCard
                            icon="pi-wallet"
                            label="Company Balance"
                            value={loading ? '…' : companyBalance.toLocaleString()}
                            description="Credits available to transfer"
                        />
                        <KpiCard
                            icon="pi-users"
                            label="Total Employees"
                            value={loading ? '…' : totalRecords.toLocaleString()}
                            description="Employees in your company"
                        />
                    </div>

                    {error && (
                        <Message severity="error" text={error} className="w-full mb-3" />
                    )}

                    <Card>
                        {/* Filter bar — 5-slot grid: 1 filter + 3 spacers + action block */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-12 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={globalFilter}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
                                            placeholder="Search by name or email"
                                            className="w-full p-inputtext-sm"
                                            aria-label="Search employees"
                                        />
                                    </IconField>
                                </div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                        onClick={clearFilters}
                                        disabled={!globalFilter}
                                    />
                                    <Button
                                        label="Refresh"
                                        icon="pi pi-refresh"
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                        onClick={handleFilterChange}
                                        loading={loading}
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={employees}
                            lazy
                            loading={loading}
                            paginator
                            first={first}
                            rows={rows}
                            totalRecords={totalRecords}
                            onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                setSortField(e.sortField);
                                setSortOrder(e.sortOrder);
                                setFirst(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="flex"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-users pi-2x text-400 mb-3" />
                                    <span className="text-600">No employees found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="name"
                                header="Employee"
                                body={nameBodyTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '220px' }}
                            />
                            <Column
                                field="credit_balance"
                                header="Credit Balance"
                                body={balanceBodyTemplate}
                                sortable
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                header="Actions"
                                body={actionBodyTemplate}
                                exportable={false}
                                style={{ minWidth: '320px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>

            {/* Transfer Credits Dialog */}
            <Dialog
                visible={transferDialogVisible}
                onHide={() => { if (!transferring) closeTransferDialog(); }}
                header="Transfer Credits"
                modal
                draggable={false}
                closable={!transferring}
                style={{ width: '90vw', maxWidth: '500px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={closeTransferDialog}
                            disabled={transferring}
                        />
                        <Button
                            label="Transfer"
                            icon="pi pi-check"
                            onClick={handleTransfer}
                            loading={transferring}
                            severity="success"
                            size="small"
                        />
                    </div>
                }
            >
                {selectedEmployee && (
                    <>
                        <div className="mb-3 p-3 surface-100 border-round">
                            <div className="font-medium text-900 mb-1">{selectedEmployee.name}</div>
                            <div className="text-600 text-sm">{selectedEmployee.email}</div>
                            <div className="text-500 text-sm mt-2">
                                Current Balance:{' '}
                                <span className="font-semibold text-primary">
                                    {selectedEmployee.credit_balance || 0} credits
                                </span>
                            </div>
                            <div className="text-500 text-sm mt-1">
                                Company pool:{' '}
                                <span className="font-semibold text-primary">
                                    {companyBalance.toLocaleString()} credits
                                </span>
                            </div>
                        </div>

                        <div className="field mb-3">
                            <label htmlFor="transfer-credits" className="block text-sm font-medium text-700 mb-2">
                                Credits Amount <span className="text-red-500">*</span>
                            </label>
                            <InputNumber
                                id="transfer-credits"
                                value={transferCredits}
                                onValueChange={(e) => setTransferCredits(e.value)}
                                min={1}
                                max={companyBalance || 999999}
                                inputMode="numeric"
                                className={classNames('w-full p-inputtext-sm', {
                                    'p-invalid': transferSubmitted && (!transferCredits || transferCredits <= 0),
                                })}
                                placeholder="Enter credits amount"
                            />
                            {transferSubmitted && (!transferCredits || transferCredits <= 0) && (
                                <small className="p-error block mt-1" role="alert">
                                    <i className="pi pi-exclamation-circle mr-1" />
                                    Please enter a valid credits amount
                                </small>
                            )}
                        </div>

                        <div className="field mb-3">
                            <label htmlFor="transfer-reason" className="block text-sm font-medium text-700 mb-2">
                                Reason
                            </label>
                            <InputText
                                id="transfer-reason"
                                value={transferReason}
                                onChange={(e) => setTransferReason(e.target.value)}
                                className="w-full p-inputtext-sm"
                                placeholder="e.g., Company admin credit allocation"
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="transfer-note" className="block text-sm font-medium text-700 mb-2">
                                Note (optional)
                            </label>
                            <InputTextarea
                                id="transfer-note"
                                value={transferNote}
                                onChange={(e) => setTransferNote(e.target.value)}
                                rows={3}
                                className="w-full p-inputtext-sm"
                                placeholder="Additional notes..."
                            />
                        </div>
                    </>
                )}
            </Dialog>

            {/* Reclaim Credits Dialog */}
            <Dialog
                visible={reclaimDialogVisible}
                onHide={() => { if (!reclaiming) closeReclaimDialog(); }}
                header="Reclaim Credits"
                modal
                draggable={false}
                closable={!reclaiming}
                style={{ width: '90vw', maxWidth: '500px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={closeReclaimDialog}
                            disabled={reclaiming}
                        />
                        <Button
                            label="Reclaim"
                            icon="pi pi-check"
                            onClick={handleReclaim}
                            loading={reclaiming}
                            severity="warning"
                            size="small"
                        />
                    </div>
                }
            >
                {selectedEmployee && (
                    <>
                        <div className="mb-3 p-3 surface-100 border-round">
                            <div className="font-medium text-900 mb-1">{selectedEmployee.name}</div>
                            <div className="text-600 text-sm">{selectedEmployee.email}</div>
                            <div className="text-500 text-sm mt-2">
                                Current Balance:{' '}
                                <span className="font-semibold text-primary">
                                    {selectedEmployee.credit_balance || 0} credits
                                </span>
                            </div>
                        </div>

                        <Message
                            severity="warn"
                            text="Reclaiming credits will deduct them from the employee's balance and return them to the company pool."
                            className="w-full mb-3"
                        />

                        <div className="field mb-3">
                            <label htmlFor="reclaim-credits" className="block text-sm font-medium text-700 mb-2">
                                Credits Amount <span className="text-red-500">*</span>
                            </label>
                            <InputNumber
                                id="reclaim-credits"
                                value={reclaimCreditsAmount}
                                onValueChange={(e) => setReclaimCreditsAmount(e.value)}
                                min={1}
                                max={selectedEmployee.credit_balance || 999999}
                                inputMode="numeric"
                                className={classNames('w-full p-inputtext-sm', {
                                    'p-invalid': reclaimSubmitted && (!reclaimCreditsAmount || reclaimCreditsAmount <= 0),
                                })}
                                placeholder="Enter credits amount"
                            />
                            {reclaimSubmitted && (!reclaimCreditsAmount || reclaimCreditsAmount <= 0) && (
                                <small className="p-error block mt-1" role="alert">
                                    <i className="pi pi-exclamation-circle mr-1" />
                                    Please enter a valid credits amount
                                </small>
                            )}
                        </div>

                        <div className="field mb-3">
                            <label htmlFor="reclaim-reason" className="block text-sm font-medium text-700 mb-2">
                                Reason <span className="text-red-500">*</span>{' '}
                                <span className="text-500 font-normal">(min 5 characters)</span>
                            </label>
                            <InputText
                                id="reclaim-reason"
                                value={reclaimReason}
                                onChange={(e) => setReclaimReason(e.target.value)}
                                className={classNames('w-full p-inputtext-sm', {
                                    'p-invalid': reclaimSubmitted && reclaimReason.trim().length < 5,
                                })}
                                placeholder="e.g., Unused credits recovery"
                                maxLength={255}
                            />
                            {reclaimSubmitted && reclaimReason.trim().length < 5 && (
                                <small className="p-error block mt-1" role="alert">
                                    <i className="pi pi-exclamation-circle mr-1" />
                                    Reason is required (minimum 5 characters)
                                </small>
                            )}
                        </div>

                        <div className="field">
                            <label htmlFor="reclaim-note" className="block text-sm font-medium text-700 mb-2">
                                Note (optional)
                            </label>
                            <InputTextarea
                                id="reclaim-note"
                                value={reclaimNote}
                                onChange={(e) => setReclaimNote(e.target.value)}
                                rows={3}
                                className="w-full p-inputtext-sm"
                                placeholder="Additional notes..."
                            />
                        </div>
                    </>
                )}
            </Dialog>

            {/* Transaction History Dialog */}
            <Dialog
                visible={transactionsDialog}
                onHide={closeTransactionsDialog}
                header="Transaction History"
                modal
                draggable={false}
                maximizable
                style={{ width: '95vw', maxWidth: '1100px' }}
            >
                {transactionsEmployee && (
                    <>
                        <div className="mb-3 p-3 surface-100 border-round">
                            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
                                <div>
                                    <div className="font-medium text-900">{transactionsEmployee.name}</div>
                                    <div className="text-600 text-sm">{transactionsEmployee.email}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-500 text-xs">Current Balance</div>
                                    <div className="text-primary font-semibold text-xl">
                                        {(transactionsEmployee.credit_balance || 0).toLocaleString()} credits
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Inner filter bar — 5-slot grid: 3 filters + 1 spacer + reset */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-12 md:col">
                                    <Dropdown
                                        value={txnTypeFilter}
                                        options={[
                                            { label: 'All Types', value: null },
                                            { label: 'Credits', value: 1 },
                                            { label: 'Debits', value: 2 },
                                        ]}
                                        onChange={(e) => setTxnTypeFilter(e.value)}
                                        placeholder="All Types"
                                        showClear
                                        className="w-full p-inputtext-sm"
                                        aria-label="Filter by transaction type"
                                    />
                                </div>
                                <div className="col-6 md:col">
                                    <Calendar
                                        value={txnDateFrom}
                                        onChange={(e) => setTxnDateFrom(e.value)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="From Date"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        maxDate={txnDateTo || new Date()}
                                        aria-label="Filter from date"
                                    />
                                </div>
                                <div className="col-6 md:col">
                                    <Calendar
                                        value={txnDateTo}
                                        onChange={(e) => setTxnDateTo(e.value)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="To Date"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        minDate={txnDateFrom}
                                        maxDate={new Date()}
                                        aria-label="Filter to date"
                                    />
                                </div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col">
                                    <Button
                                        label="Reset"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        onClick={resetTransactionFilters}
                                        className="w-full py-1 md:py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={transactions}
                            lazy
                            paginator
                            first={transactionsFirst}
                            rows={transactionsRows}
                            totalRecords={transactionsTotalRecords}
                            onPage={onTransactionsPage}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            loading={transactionsLoading}
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="500px"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-inbox pi-2x text-400 mb-3" />
                                    <span className="text-600">No transactions found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="created_at"
                                header="Date & Time"
                                body={transactionDateTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                field="type"
                                header="Type"
                                body={transactionTypeTemplate}
                                style={{ minWidth: '110px' }}
                            />
                            <Column
                                field="credits"
                                header="Amount"
                                body={transactionAmountTemplate}
                                sortable
                                style={{ minWidth: '110px' }}
                            />
                            <Column
                                field="transfer_type"
                                header="Transfer Type"
                                body={transferTypeTemplate}
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                header="Transferred From"
                                body={(rowData) => partyTemplate(rowData.transferred_from)}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                header="Transferred To"
                                body={(rowData) => partyTemplate(rowData.transferred_to)}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                header="Initiated By"
                                body={(rowData) => partyTemplate(rowData.initiated_by)}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="reason"
                                header="Reason"
                                body={(rowData) => rowData.reason || <span className="text-500">-</span>}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="note"
                                header="Note"
                                body={(rowData) => rowData.note || <span className="text-500">-</span>}
                                style={{ minWidth: '200px' }}
                            />
                        </DataTable>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default CompanyCreditManagement;
